// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { DataTable, DataTableBody, Icon } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import RequiredProductRow from "@steiner/App/Components/ProductDetails/RequiredProductRow";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { DataTablePresentationProps } from "@steiner/MobiusOverrides/DataTable";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState, ownProps: HasProductContext) => ({
    products: getProductsForProductInfoList(state, ownProps.productContext.product.id),
});

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

export interface RequiredProductsStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    titleIcon?: IconPresentationProps;
    table?: DataTablePresentationProps;
}

export const requiredProductsStyles: RequiredProductsStyles = {};

const styles = requiredProductsStyles;

const RequiredProducts = ({
    products,
    loadCarouselProducts,
    productContext,
}: Props) => {
    const shellContext = useShellContext();

    useEffect(() => {
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: productContext?.product?.id,
            carouselType: "relatedProducts",
            relatedProductType: "required",
            seedWithManuallyAssigned: "",
            displayProductsFrom: "allCategories",
            selectedCategoryIds: [],
            numberOfProductsToDisplay: 10,
            widgetPosition: 1,
            productId: productContext?.product?.id,
            isProductDetailsPage: true,
            isProductListPage: false,
            isBrandDetailsPage: false
        });
    }, [productContext.product.id]);

    if (!products || products.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.title}>
                <Icon {...styles.titleIcon} />
                {translate("Required Products")}
            </Typography>
            <DataTable {...styles.table}>
                <DataTableBody>
                    {products.map(product => {
                        return (
                            <RequiredProductRow key={product.id} product={product} carouselId={productContext?.product?.id} />
                        );
                    })}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(RequiredProducts));